import React from 'react'
import { theme } from 'antd'
import { SKELETON_DELAY_MS } from './skeletonTiming'

/**
 * The few rules `DataGrid` cannot express as inline styles. Internal to `DataGrid`, which renders it
 * itself - not exported, so there is nothing for a caller to wire up or forget.
 *
 * A `<style>` element rather than a LESS file in `src/styles/`, so the grid carries its own rules.
 * The reduced-motion guard lives INSIDE the CSS, never in a JS `matchMedia` branch - a JS branch
 * gets asserted in jsdom against a mock that always answers false, i.e. never actually tested.
 */
export function DataGridStyles() {
  const { token } = theme.useToken()

  return (
    <style>{`
      /* The row fill, first, so any cell-level state a consumer paints sits over it. */
      tr.vt-grid-row:hover > td {
        background: ${token.colorFillTertiary};
      }
      tr.vt-grid-row:hover > td.vt-grid-sticky {
        /* A sticky cell scrolls over content, so its hover fill cannot be translucent - but it must
           still land on the SAME pixel the translucent one does, or the row splits at the freeze
           line. Painting the alpha over the container's own fill is opaque AND identical; a surface
           token instead (colorBgLayout) inverts against colorFillTertiary in the dark theme. */
        background-color: ${token.colorBgContainer};
        background-image: linear-gradient(${token.colorFillTertiary}, ${token.colorFillTertiary});
      }
      /* Scoped to the body: a pinned HEADER cell wears this same class - it is what lets the row
         hover repaint a frozen cell - and the header paints its own fill inline. */
      tbody .vt-grid-sticky {
        background: ${token.colorBgContainer};
      }

      @keyframes vtGridCellSaved {
        0% { background-color: ${token.colorSuccessBg}; }
        60% { background-color: ${token.colorSuccessBg}; }
        100% { background-color: transparent; }
      }
      /* Cell states paint on an overlay across the whole td, not on the content: the td's
         overflow: hidden (there for the ellipsis) clips a ring drawn on the text. Rings are INSET,
         since an outset ring paints over the neighbouring cell. */
      .vt-grid-cell-ring {
        position: absolute;
        inset: 0;
        z-index: 0;
        border-radius: 4px;
        pointer-events: none;
        transition: box-shadow 120ms ease-out, background-color 120ms ease-out;
      }
      .vt-grid-cell-content {
        position: relative;
        z-index: 1;
      }
      .vt-grid-cell:hover > .vt-grid-cell-ring {
        box-shadow: inset 0 0 0 1px ${token.colorBorderSecondary};
        background: ${token.colorBgContainer};
      }
      .vt-grid-cell:focus-visible {
        outline: none;
      }
      .vt-grid-cell:focus-visible > .vt-grid-cell-ring {
        box-shadow: inset 0 0 0 1px ${token.colorPrimary};
        background: ${token.colorBgContainer};
      }
      /* The doubled class matches the hover rule's specificity, so the state a cell is IN wins by
         source order: the pointer is always on a cell just opened, or on a failed one being retried. */
      .vt-grid-cell.vt-grid-cell-editing > .vt-grid-cell-ring {
        box-shadow: inset 0 0 0 2px ${token.colorPrimary};
        background: ${token.colorBgContainer};
      }
      .vt-grid-cell.vt-grid-cell-saving > .vt-grid-cell-ring {
        background: ${token.colorPrimaryBg};
      }
      .vt-grid-cell.vt-grid-cell-saved > .vt-grid-cell-ring {
        animation: vtGridCellSaved 1200ms ease-out both;
      }
      .vt-grid-cell.vt-grid-cell-error > .vt-grid-cell-ring {
        box-shadow: inset 0 0 0 2px ${token.colorError};
        background: ${token.colorErrorBg};
      }

      /* An open editor draws no box of its own inside the ring. The border is removed rather than
         made transparent because antd's borderless variant still reserves 1px; !important outranks
         the !important fills in src/styles/dark-theme.less. */
      .vt-grid-cell-editing .ant-input,
      .vt-grid-cell-editing .ant-picker,
      .vt-grid-cell-editing .ant-select .ant-select-selector {
        background-color: transparent !important;
        border-color: transparent !important;
        border-width: 0 !important;
        box-shadow: none !important;
      }
      /* The value sits exactly where the read text sat, with the width the column was measured to. */
      .vt-grid-cell-editing .ant-input,
      .vt-grid-cell-editing .ant-input-number,
      .vt-grid-cell-editing .ant-input-number-input,
      .vt-grid-cell-editing .ant-select .ant-select-selector,
      .vt-grid-cell-editing .ant-picker {
        padding-inline: 0;
      }
      .vt-grid-cell-editing .ant-picker,
      .vt-grid-cell-editing .ant-input,
      .vt-grid-cell-editing .ant-select {
        width: 100%;
      }

      /* Reserved, never conditionally rendered, so a reveal never reflows the row; OPACITY so the
         icon stays in the tab order. Padding plus matching negative margin grows the touch target. */
      .vt-grid-cell-affordance {
        opacity: 0;
        transition: opacity 120ms ease-out;
        padding: 10px;
        margin: -10px;
        color: ${token.colorTextTertiary};
      }
      /* The row tier reveals which cells can be edited - and which are locked - at a glance; a save
         result shows whether or not the pointer is still there. */
      tr.vt-grid-row:hover .vt-grid-cell-affordance,
      tr.vt-grid-row:focus-within .vt-grid-cell-affordance,
      .vt-grid-cell-saving .vt-grid-cell-affordance,
      .vt-grid-cell-saved .vt-grid-cell-affordance,
      .vt-grid-cell-error .vt-grid-cell-affordance {
        opacity: 1;
      }
      /* The cell tier steps the colour rather than the opacity: colorTextTertiary at reduced opacity
         falls under WCAG 1.4.11's 3:1, colorTextSecondary clears it in both themes. */
      .vt-grid-cell:hover .vt-grid-cell-affordance,
      .vt-grid-cell:focus-within .vt-grid-cell-affordance,
      .vt-grid-cell:focus-visible .vt-grid-cell-affordance,
      .vt-grid-cell-editing .vt-grid-cell-affordance,
      .vt-grid-cell-quiet:hover .vt-grid-cell-affordance,
      .vt-grid-cell-quiet:focus-within .vt-grid-cell-affordance,
      .vt-grid-cell-affordance:focus-visible {
        opacity: 1;
        color: ${token.colorTextSecondary};
      }
      @media (hover: none) {
        .vt-grid-cell-affordance { opacity: 1; }
      }

      /* OPACITY, never display or visibility: either of those drops the trigger out of the tab
         order. The opacity sits on the cell's CONTENT, not the cell - the cell can be pinned right,
         and an invisible sticky cell would let the scrolling columns show through it. */
      tr.vt-grid-row td.vt-grid-row-actions > * {
        opacity: 0;
        transition: opacity 120ms ease-out;
      }
      tr.vt-grid-row:hover td.vt-grid-row-actions > *,
      tr.vt-grid-row:focus-within td.vt-grid-row-actions > * {
        opacity: 1;
      }
      @media (hover: none) {
        tr.vt-grid-row td.vt-grid-row-actions > * { opacity: 1; }
      }

      /* The bar is drawn but invisible until the load has held for SKELETON_DELAY_MS - that is what
         'backwards' fill buys, so a load that resolves inside the delay shows no placeholder at all.
         The rows stay mounted the whole time, reserving the table's height. */
      @keyframes vtGridSkeletonIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes vtGridSkeletonSweep {
        from { transform: translateX(-100%); }
        to { transform: translateX(100%); }
      }
      .vt-grid-skeleton-bar {
        position: relative;
        overflow: hidden;
        background: ${token.colorFillTertiary};
        border-radius: 4px;
        animation: vtGridSkeletonIn 120ms ease-out ${SKELETON_DELAY_MS}ms backwards;
      }
      /* The sweep rides a pseudo-element's transform rather than the bar's own background-position:
         a background-position animation repaints every bar on the main thread each frame, and a full
         grid holds well over a hundred of them. The band is a fill ADDED over the base, because the
         tokens are alpha fills and nothing can subtract. */
      .vt-grid-skeleton-bar::after {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(90deg, transparent 0%, ${token.colorFillQuaternary} 50%, transparent 100%);
        animation: vtGridSkeletonSweep 1600ms ease-in-out infinite;
        /* Negative, so no bar ever waits: each row starts one step further into the sweep than the
           row above, and the highlight travels down the grid instead of every bar pulsing together. */
        animation-delay: calc(var(--vt-grid-row-index, 0) * -90ms);
      }

      /* On the ROW, never on a cell: opacity on a sticky cell lets the columns scrolling under it
         show through, while opacity on the row renders every cell as one group before compositing.
         Opacity only - a transform here would make the row the containing block for its own
         positioned descendants, and sticky is clamped to a transformed ancestor. */
      @keyframes vtGridRowIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      tr.vt-grid-row {
        animation: vtGridRowIn 180ms ease-out backwards;
        animation-delay: min(calc(var(--vt-grid-row-index, 0) * 14ms), 140ms);
      }

      @media (prefers-reduced-motion: reduce) {
        tr.vt-grid-row td.vt-grid-row-actions > * { transition: none; }
        .vt-grid-cell-ring { transition: none; }
        .vt-grid-cell.vt-grid-cell-saved > .vt-grid-cell-ring { animation: none; }
        .vt-grid-cell-affordance { transition: none; }
        /* The bar's fade-in stays - its delay is the anti-flash, a function rather than
           decoration. Only the travelling sweep and the row reveal go. */
        .vt-grid-skeleton-bar::after { display: none; }
        tr.vt-grid-row { animation: none; }
      }
    `}</style>
  )
}
